export const CHARS_PER_TOKEN = 4;
export const DRAWER_BASELINE_MIN_RECEIPTS = 5;

export const TOKEN_SAVINGS_SURFACES = [
  "chat",
  "mcp",
  "agents",
  "workflows",
  "deep_research",
  "compare_models",
  "gmail",
  "fireflies",
  "clickup_inbox",
  "telegram",
  "zoom",
  "ringcentral",
  "fireflies_format",
  "clickup_resolve",
  "daily_digest",
  "smart_tasks",
  "creative_studio",
  "auto_title",
  "other",
] as const;

export type TokenSavingsSurface = (typeof TOKEN_SAVINGS_SURFACES)[number];

export const TOKEN_SAVINGS_LEVERS = [
  "drawer",
  "crisis_stop",
  "private_hold",
  "rag_pack",
  "history_trim",
  "librarian_pack",
  "doc_clip",
  "none",
] as const;

export type TokenSavingsLever = (typeof TOKEN_SAVINGS_LEVERS)[number];

export const TOKEN_SAVINGS_SURFACE_LABELS: Record<TokenSavingsSurface, string> = {
  chat: "Chat",
  mcp: "MCP retrieval",
  agents: "AI Agents",
  workflows: "Workflows",
  deep_research: "Deep Research",
  compare_models: "Compare models",
  gmail: "Gmail",
  fireflies: "Fireflies",
  clickup_inbox: "ClickUp inbox",
  telegram: "Telegram",
  zoom: "Zoom",
  ringcentral: "RingCentral",
  fireflies_format: "Fireflies format",
  clickup_resolve: "ClickUp resolve",
  daily_digest: "Daily Digest",
  smart_tasks: "Smart Tasks",
  creative_studio: "Creative Studio",
  auto_title: "Thread titles",
  other: "Other",
};

export function estimateTokens(text: string): number {
  if (!text) return 0;
  return Math.ceil(text.length / CHARS_PER_TOKEN);
}

export function estimateTokensFromChars(chars: number): number {
  if (!Number.isFinite(chars) || chars <= 0) return 0;
  return Math.ceil(chars / CHARS_PER_TOKEN);
}

export function tokensSaved(
  baseline: number | null | undefined,
  actual: number | null | undefined,
): number {
  const b = typeof baseline === "number" && Number.isFinite(baseline) ? baseline : 0;
  const a = typeof actual === "number" && Number.isFinite(actual) ? actual : 0;
  return Math.max(0, Math.floor(b) - Math.floor(a));
}

/** Returns a 0–1 fraction when baseline is measured; otherwise null (do not invent %). */
export function savingsPercent(
  baseline: number | null | undefined,
  actual: number | null | undefined,
): number | null {
  if (baseline == null || !Number.isFinite(baseline) || baseline <= 0) return null;
  return tokensSaved(baseline, actual) / baseline;
}

export function formatCompactTokens(n: number): string {
  const abs = Math.abs(Math.round(n));
  if (abs < 1000) return String(abs);
  if (abs < 1_000_000) {
    const k = abs / 1000;
    return `${k >= 100 ? k.toFixed(0) : k.toFixed(1).replace(/\.0$/, "")}k`;
  }
  const m = abs / 1_000_000;
  return `${m >= 100 ? m.toFixed(0) : m.toFixed(1).replace(/\.0$/, "")}M`;
}

export function isTokenSavingsSurface(value: string): value is TokenSavingsSurface {
  return (TOKEN_SAVINGS_SURFACES as readonly string[]).includes(value);
}

export type CommunicationAnalyzeContentType =
  | "email"
  | "meeting"
  | "clickup"
  | "telegram"
  | "zoom"
  | "ringcentral";

export function communicationSurface(
  contentType: CommunicationAnalyzeContentType | undefined,
): TokenSavingsSurface {
  if (contentType === "meeting") return "fireflies";
  if (contentType === "clickup") return "clickup_inbox";
  if (contentType === "telegram") return "telegram";
  if (contentType === "zoom") return "zoom";
  if (contentType === "ringcentral") return "ringcentral";
  return "gmail";
}

export function stopPathToLever(
  path: string,
): Extract<TokenSavingsLever, "drawer" | "crisis_stop" | "private_hold"> | "none" {
  if (path === "drawer" || path === "crisis_stop" || path === "private_hold") return path;
  return "none";
}
